import { capitalize } from 'lodash';
import { createColumnHelper } from '@tanstack/react-table';
import { VolumeX } from 'lucide-react';

import { formatDate } from '@/portainer/filters/filters';

import { Badge } from '@@/Badge';
import { StatusBadge, StatusBadgeType } from '@@/StatusBadge';
import { Tooltip } from '@@/Tip/Tooltip';
import { Button } from '@@/buttons';

import { AlertSeverity, ActiveAlertRow } from './types';
import { statusToBadgeType } from './utils';
import { ExpandableMessage } from './ExpandableMessage';

export const columnHelper = createColumnHelper<ActiveAlertRow>();

const thresholdColorToBadgeType: Record<AlertSeverity, StatusBadgeType> = {
  critical: 'danger',
  warning: 'warning',
};

export function createColumns(onSilence: (alert: ActiveAlertRow) => void) {
  return [
    columnHelper.accessor('alertName', {
      header: 'Alert Name',
      cell: ({ getValue, row: { original: alert } }) => (
        <>
          {getValue()}
          <Badge className="ml-2">ID: {alert.alertRuleId}</Badge>
        </>
      ),
    }),
    columnHelper.accessor('state', {
      header: () => (
        <>
          State
          <Tooltip
            position="top"
            message={
              <div className="text-xs">
                <p>Alert State represents the current status of the alert.</p>
                <p>Alerts can be in one of the following states:</p>
                <ul>
                  <li>
                    <strong>Active</strong>: The alert is currently active and
                    firing.
                  </li>
                  <li>
                    <strong>Firing</strong>: The alert rule is currently firing
                    and has triggered alerts.
                  </li>
                  <li>
                    <strong>Inactive</strong>: The alert rule is not currently
                    firing and has not triggered alerts.
                  </li>
                  <li>
                    <strong>Pending</strong>: The alert rule is currently firing
                    but has not yet triggered alerts.
                  </li>
                </ul>
              </div>
            }
          />
        </>
      ),
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          <StatusBadge color={statusToBadgeType[value]}>
            {capitalize(value)}
          </StatusBadge>
        );
      },
    }),
    columnHelper.accessor('severity', {
      header: 'Severity',
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          <StatusBadge color={thresholdColorToBadgeType[value]}>
            {capitalize(value)}
          </StatusBadge>
        );
      },
    }),
    columnHelper.accessor('message', {
      header: 'Message',
      cell: ({ getValue }) => (
        <ExpandableMessage>{getValue()}</ExpandableMessage>
      ),
    }),
    columnHelper.accessor('source', {
      header: 'Source',
    }),
    columnHelper.accessor('instance', {
      header: 'Instance',
    }),
    columnHelper.accessor(({ startsAt }) => formatDate(startsAt), {
      header: 'Started At',
      id: 'startsAt',
    }),
    columnHelper.accessor(({ updatedAt }) => formatDate(updatedAt), {
      header: 'Last Updated',
      id: 'updatedAt',
    }),
    columnHelper.accessor('receivers', {
      header: 'Receivers',
      cell: ({ getValue }) => (
        <div className="flex flex-wrap gap-1">
          {getValue().map((receiver) => (
            <Badge key={receiver} type="info">
              {receiver}
            </Badge>
          ))}
        </div>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const alert = row.original;
        return (
          <div className="flex gap-2 items-center">
            <Button
              color="default"
              size="xsmall"
              icon={VolumeX}
              onClick={() => onSilence(alert)}
              data-cy={`silence-alert-${alert.id}`}
              title="Silence this alert"
            >
              Silence
            </Button>
          </div>
        );
      },
    }),
  ];
}

// Default columns for backward compatibility
export const columns = createColumns(() => {});
